/* Generated from shared/reorder/useListReorder.js. Do not edit this copy. */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export default function useListReorder({ items, locked, onMove }) {
  const listRef = useRef(null), dragRef = useRef(null);
  const keyboardFocusRef = useRef(null);
  const overlayRef = useRef(null), dropPositionsRef = useRef(null);
  const [drop, setDrop] = useState(null);

  useLayoutEffect(() => {
    const previous = dropPositionsRef.current;
    dropPositionsRef.current = null;
    if (previous) {
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      listRef.current?.querySelectorAll('[data-reorder-id]').forEach(row => {
        // End the drag transition before measuring the row's final position.
        row.getAnimations?.().forEach(animation => animation.cancel());
        const from = previous.get(row.dataset.reorderId);
        const to = row.getBoundingClientRect();
        if (!from || reducedMotion || !row.animate) return;
        row.animate([
          { transform: `translate(${from.left - to.left}px, ${from.top - to.top}px)` },
          { transform: 'translate(0px, 0px)' },
        ], { duration: 160, easing: 'ease-out' });
      });
    }
    keyboardFocusRef.current?.focus({ preventScroll: true });
    keyboardFocusRef.current = null;
  }, [items, drop]);

  useEffect(() => {
    dragRef.current = null;
    setDrop(null);
  }, [items]);

  function captureDropPositions(itemId) {
    dropPositionsRef.current = new Map([...listRef.current.querySelectorAll('[data-reorder-id]')]
      .map(row => [row.dataset.reorderId, row.getBoundingClientRect()]));
    if (overlayRef.current) dropPositionsRef.current.set(String(itemId), overlayRef.current.getBoundingClientRect());
  }

  function cancelDrag() {
    const active = dragRef.current;
    if (active?.target) captureDropPositions(active.itemId);
    dragRef.current = null;
    setDrop(null);
  }

  function startDrag(event, itemId) {
    if (locked || items.length < 2 || dragRef.current || event.button !== 0 || event.isPrimary === false) return;
    event.preventDefault();
    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture(event.pointerId);
    // Keep the DOM order stable so the handle retains pointer capture.
    const ids = new Map(items.map(item => [String(item.id), item.id]));
    const listTop = listRef.current.getBoundingClientRect().top;
    const rows = [...listRef.current.querySelectorAll('[data-reorder-id]')].map(row => {
      const { top, left, width, height } = row.getBoundingClientRect();
      return { id: ids.get(row.dataset.reorderId), top, left, width, height };
    });
    dragRef.current = { itemId, rows, pointerId: event.pointerId,
      startX: event.clientX, startY: event.clientY, listTop, target: null };
  }

  function trackDrag(event, autoScroll = true) {
    const active = dragRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    if (!active.target && Math.abs(event.clientY - active.startY) < 5) return;
    const bounds = listRef.current.getBoundingClientRect();
    const valid = event.clientX >= bounds.left && event.clientX <= bounds.right
      && event.clientY >= bounds.top - 24 && event.clientY <= bounds.bottom + 24;
    const dragged = active.rows.find(row => row.id === active.itemId);
    const offsetY = event.clientY - active.startY;
    // Use the center of the grabbed row, in the list's original coordinate space.
    const draggedCenter = dragged.top + dragged.height / 2 + offsetY + active.listTop - bounds.top;
    const arranged = active.rows.filter(row => row !== dragged);
    const before = arranged.find(row => draggedCenter < row.top + row.height / 2);
    arranged.splice(before ? arranged.indexOf(before) : arranged.length, 0, dragged);
    let top = active.rows[0].top;
    const shifts = new Map();
    arranged.forEach(row => {
      shifts.set(row.id, valid ? top - row.top : 0);
      top += row.height;
    });
    active.target = {
      itemId: active.itemId, beforeItemId: before?.id ?? null, valid, shifts, rect: dragged,
      offsetX: event.clientX - active.startX, offsetY,
    };
    setDrop(active.target);
    if (autoScroll && event.clientY < 60) window.scrollBy(0, -12);
    else if (autoScroll && event.clientY > window.innerHeight - 60) window.scrollBy(0, 12);
  }

  function finishDrag(event) {
    const active = dragRef.current;
    if (!active || active.pointerId !== event.pointerId) return;
    trackDrag(event, false);
    cancelDrag();
    if (!locked && active.target?.valid) onMove(active.itemId, active.target.beforeItemId);
  }

  function cancelPointer(event) {
    if (dragRef.current?.pointerId === event.pointerId) cancelDrag();
  }

  function reorderWithKeyboard(event, itemId) {
    if (event.key === 'Escape') {
      cancelDrag();
      return;
    }
    if (locked || dragRef.current || items.length < 2 || !['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    keyboardFocusRef.current = event.currentTarget;
    const index = items.findIndex(item => item.id === itemId);
    if (event.key === 'ArrowUp' && index > 0) onMove(itemId, items[index - 1].id);
    if (event.key === 'ArrowDown' && index < items.length - 1) onMove(itemId, items[index + 2]?.id ?? null);
    if (event.key === 'Home' && index > 0) onMove(itemId, items[0].id);
    if (event.key === 'End' && index < items.length - 1) onMove(itemId, null);
  }

  function handleProps(id) {
    return {
      onPointerDown: event => startDrag(event, id), onPointerMove: trackDrag, onPointerUp: finishDrag,
      onPointerCancel: cancelPointer, onLostPointerCapture: cancelPointer, onKeyDown: event => reorderWithKeyboard(event, id),
    };
  }

  function rowProps(id) {
    return { 'data-reorder-id': id, style: {
      transition: drop ? undefined : 'none',
      transform: drop && drop.itemId !== id ? `translateY(${drop.shifts.get(id)}px)` : undefined,
    } };
  }

  const overlayStyle = drop && {
    left: drop.rect.left + drop.offsetX, top: drop.rect.top + drop.offsetY,
    width: drop.rect.width, minHeight: drop.rect.height,
  };
  return { listRef, overlayRef, drop, handleProps, rowProps, overlayStyle };
}
